import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface AudioPlayerState {
  currentUrl: string | null;
  isPlaying: boolean;
  isLoading: boolean;
  progress: number;
  duration: number;
}

interface AudioPlayerContextType {
  state: AudioPlayerState;
  play: (url: string) => void;
  pause: () => void;
  stop: () => void;
  toggle: (url: string) => void;
  isCurrentTrack: (url: string) => boolean;
  getProgress: (url: string) => number;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: React.ReactNode;
}

// SoundCloud Widget API types
declare global {
  interface Window {
    SC: {
      Widget: {
        (iframe: HTMLIFrameElement): SCWidget;
        Events: {
          READY: string;
          PLAY: string;
          PAUSE: string;
          FINISH: string;
          PLAY_PROGRESS: string;
        };
      };
    };
  }
}

interface SCWidget {
  bind: (event: string, callback: (data?: any) => void) => void;
  unbind: (event: string) => void;
  load: (url: string, options?: object) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  getDuration: (callback: (duration: number) => void) => void;
  getPosition: (callback: (position: number) => void) => void;
  seekTo: (milliseconds: number) => void;
}

// Clean SoundCloud URL by removing tracking parameters
const cleanSoundCloudUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Remove all query parameters (tracking params like si, utm_source, etc.)
    urlObj.search = '';
    return urlObj.toString();
  } catch {
    return url;
  }
};

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [state, setState] = useState<AudioPlayerState>({
    currentUrl: null,
    isPlaying: false,
    isLoading: false,
    progress: 0,
    duration: 0,
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const widgetRef = useRef<SCWidget | null>(null);
  const widgetReadyRef = useRef(false);
  const pendingUrlRef = useRef<string | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  // Load SoundCloud Widget SDK
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.SC) {
      const script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      script.async = true;
      script.onload = () => {
        setSdkLoaded(true);
      };
      document.body.appendChild(script);
    } else if (window.SC) {
      setSdkLoaded(true);
    }
  }, []);

  // Initialize widget once when SDK is loaded
  useEffect(() => {
    if (sdkLoaded && iframeRef.current && !widgetRef.current) {
      widgetRef.current = window.SC.Widget(iframeRef.current);

      widgetRef.current.bind(window.SC.Widget.Events.READY, () => {
        widgetReadyRef.current = true;
        // If there's a pending URL, load it now
        if (pendingUrlRef.current) {
          const url = cleanSoundCloudUrl(pendingUrlRef.current);
          pendingUrlRef.current = null;
          widgetRef.current?.load(url, {
            auto_play: true,
            show_artwork: false,
            show_user: false,
            show_playcount: false,
            show_comments: false,
            hide_related: true,
            visual: false,
            buying: false,
            liking: false,
            download: false,
            sharing: false,
          });
        }
      });

      widgetRef.current.bind(window.SC.Widget.Events.PLAY, () => {
        setState(prev => ({ ...prev, isPlaying: true, isLoading: false }));
      });

      widgetRef.current.bind(window.SC.Widget.Events.PAUSE, () => {
        setState(prev => ({ ...prev, isPlaying: false }));
      });

      widgetRef.current.bind(window.SC.Widget.Events.FINISH, () => {
        setState(prev => ({ ...prev, isPlaying: false, progress: 0 }));
      });

      widgetRef.current.bind(window.SC.Widget.Events.PLAY_PROGRESS, (data: { currentPosition: number; relativePosition: number }) => {
        setState(prev => ({
          ...prev,
          progress: data.relativePosition * 100,
        }));
      });
    }
  }, [sdkLoaded]);

  // Load track when currentUrl changes
  useEffect(() => {
    if (!state.currentUrl) return;

    const cleanUrl = cleanSoundCloudUrl(state.currentUrl);

    if (widgetReadyRef.current && widgetRef.current) {
      widgetRef.current.load(cleanUrl, {
        auto_play: true,
        show_artwork: false,
        show_user: false,
        show_playcount: false,
        show_comments: false,
        hide_related: true,
        visual: false,
        buying: false,
        liking: false,
        download: false,
        sharing: false,
      });
    } else {
      // Widget not ready yet, store URL to load when ready
      pendingUrlRef.current = cleanUrl;
    }
  }, [state.currentUrl]);

  const play = useCallback((url: string) => {
    setState(prev => ({
      ...prev,
      currentUrl: url,
      isPlaying: true,
      progress: prev.currentUrl !== url ? 0 : prev.progress,
    }));
  }, []);

  const pause = useCallback(() => {
    if (widgetRef.current) {
      widgetRef.current.pause();
    }
    setState(prev => ({
      ...prev,
      isPlaying: false,
    }));
  }, []);

  const stop = useCallback(() => {
    if (widgetRef.current) {
      widgetRef.current.pause();
    }
    setState({
      currentUrl: null,
      isPlaying: false,
      isLoading: false,
      progress: 0,
      duration: 0,
    });
  }, []);

  const toggle = useCallback((url: string) => {
    // If clicking on a different track, switch to it
    if (state.currentUrl !== url) {
      setState(prev => ({
        ...prev,
        currentUrl: url,
        isPlaying: false,  // Will be set to true when PLAY event fires
        isLoading: true,   // Show loading state until track actually plays
        progress: 0,
      }));
      return;
    }

    // If clicking on the same track, toggle play/pause
    const newIsPlaying = !state.isPlaying;

    // Update state immediately for responsive UI
    setState(prev => ({
      ...prev,
      isPlaying: newIsPlaying,
    }));

    // Also control the widget if available
    if (widgetRef.current) {
      if (newIsPlaying) {
        widgetRef.current.play();
      } else {
        widgetRef.current.pause();
      }
    }
  }, [state.currentUrl, state.isPlaying]);

  const isCurrentTrack = useCallback((url: string) => {
    return state.currentUrl === url;
  }, [state.currentUrl]);

  const getProgress = useCallback((url: string) => {
    return state.currentUrl === url ? state.progress : 0;
  }, [state.currentUrl, state.progress]);

  const contextValue: AudioPlayerContextType = {
    state,
    play,
    pause,
    stop,
    toggle,
    isCurrentTrack,
    getProgress,
  };

  // Static iframe URL - we'll use load() API to change tracks
  const widgetUrl = 'https://w.soundcloud.com/player/?url=&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false';

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      {/* Hidden SoundCloud iframe widget */}
      <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }}>
        <iframe
          ref={iframeRef}
          id="sc-widget"
          title="SoundCloud Player"
          width="100%"
          height="166"
          scrolling="no"
          frameBorder="no"
          allow="autoplay"
          src={widgetUrl}
        />
      </div>
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;
